import { useCallback } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

interface ProfileHierarchy {
  id: string;
  name: string;
  first_name: string | null;
  last_name: string | null;
  country: string | null;
  avatar_url: string | null;
  is_primary: boolean;
  profile_type: 'primary' | 'standard' | 'kids';
  display_order: number;
  profile_settings: any;
  is_active: boolean;
  total_profiles: number;
  created_at: string;
}

interface ProfileQueryData {
  profiles: ProfileHierarchy[];
  currentProfileId: string | null;
}

export function useProfileHierarchy() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const userId = session?.user?.id;

  // Single cached query for all profile data
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['userProfiles', userId],
    queryFn: async (): Promise<ProfileQueryData> => {
      if (!userId) {
        return { profiles: [], currentProfileId: null };
      }

      // Fetch profiles and current profile in parallel
      const [profilesResult, currentResult] = await Promise.all([
        supabase
          .from('user_profiles')
          .select('id, user_id, name, avatar_url, is_primary, profile_type, display_order, profile_settings, first_name, last_name, country, anonymous_mode, onboarding_completed, created_at')
          .eq('user_id', userId)
          .order('is_primary', { ascending: false })
          .order('display_order'),
        supabase
          .from('user_current_profile')
          .select('current_profile_id')
          .eq('user_id', userId)
          .maybeSingle()
      ]);

      if (profilesResult.error) {
        console.error('Error fetching profiles:', profilesResult.error);
        throw profilesResult.error;
      }

      const userProfiles = profilesResult.data || [];
      const currentProfileId = currentResult.data?.current_profile_id || null;

      // Transform to expected format
      const transformedProfiles: ProfileHierarchy[] = userProfiles.map(profile => ({
        id: profile.id,
        name: profile.name,
        first_name: profile.first_name ?? null,
        last_name: profile.last_name ?? null,
        country: profile.country ?? null,
        avatar_url: profile.avatar_url,
        is_primary: profile.is_primary,
        profile_type: profile.profile_type as 'primary' | 'standard' | 'kids',
        display_order: profile.display_order,
        profile_settings: profile.profile_settings,
        is_active: currentProfileId === profile.id,
        total_profiles: userProfiles.length,
        created_at: profile.created_at,
      }));

      return { profiles: transformedProfiles, currentProfileId };
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes - profiles don't change often
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const profiles = data?.profiles || [];
  const primaryProfile = profiles.find(p => p.is_primary) || null;
  const activeProfile = profiles.find(p => p.is_active) || primaryProfile;

  // Single realtime listener - invalidates query cache instead of direct refetch
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel(`profile-hierarchy-${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_profiles',
          filter: `user_id=eq.${userId}`
        },
        () => {
          // Invalidate cache - React Query will refetch if component is mounted
          queryClient.invalidateQueries({ queryKey: ['userProfiles', userId] });
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_current_profile',
          filter: `user_id=eq.${userId}`
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['userProfiles', userId] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);

  const switchProfile = useCallback(async (profileId: string) => {
    if (!userId) return;

    try {
      await supabase
        .from('user_current_profile')
        .upsert({
          user_id: userId,
          current_profile_id: profileId
        });

      // Optimistically update the cache
      queryClient.setQueryData(['userProfiles', userId], (old: ProfileQueryData | undefined) => {
        if (!old) return old;
        return {
          ...old,
          currentProfileId: profileId,
          profiles: old.profiles.map(p => ({
            ...p,
            is_active: p.id === profileId
          }))
        };
      });
    } catch (error) {
      console.error('Error switching profile:', error);
    }
  }, [userId, queryClient]);

  const updateProfileOrder = useCallback(async (profileId: string, newOrder: number) => {
    if (!userId) return;

    try {
      await supabase
        .from('user_profiles')
        .update({ display_order: newOrder })
        .eq('id', profileId)
        .eq('user_id', userId);

      // Invalidate to refetch with new order
      queryClient.invalidateQueries({ queryKey: ['userProfiles', userId] });
    } catch (error) {
      console.error('Error updating profile order:', error);
    }
  }, [userId, queryClient]);

  const fetchProfileHierarchy = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    profiles,
    primaryProfile,
    activeProfile,
    isLoading,
    fetchError: error?.message || null,
    fetchProfileHierarchy,
    switchProfile,
    updateProfileOrder
  };
}
